import React, { useEffect, useMemo, useState } from 'react';
import SuccessToast from '../../components/successToast';
import ValidationErrorToast from '../../components/validationErrorToast';
import InterviewSidebar from './InterviewSidebar';
import { styles } from './interviewStyles';

const STORAGE_KEY = 'mploi_interview_question_sets';

interface QuestionSet {
  id: string;
  name: string;
  description: string;
  questions: string[];
  createdAt: string;
  updatedAt: string;
}

const fieldLabel: React.CSSProperties = { display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' };

const normaliseQuestions = (raw: string): string[] => raw
  .split(/\r?\n/)
  .map((line) => line.trim())
  .filter(Boolean)
  .slice(0, 50);

const InterviewQuestionSets: React.FC = () => {
  const [sets, setSets] = useState<QuestionSet[]>([]);
  const [editingId, setEditingId] = useState<string>('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [questionsText, setQuestionsText] = useState('');
  const questions = useMemo(() => normaliseQuestions(questionsText), [questionsText]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setSets(JSON.parse(saved));
    } catch (error) {
      console.error('Failed to load question sets:', error);
    }
  }, []);

  const persist = (nextSets: QuestionSet[]) => {
    setSets(nextSets);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSets));
  };

  const clearForm = () => {
    setEditingId('');
    setName('');
    setDescription('');
    setQuestionsText('');
  };

  const saveSet = () => {
    if (!name.trim()) return ValidationErrorToast('Required', 'Question set name is required');
    if (questions.length === 0) return ValidationErrorToast('Required', 'Add at least one question, one per line');

    const now = new Date().toISOString();
    if (editingId) {
      persist(sets.map((set) => set.id === editingId ? { ...set, name: name.trim(), description: description.trim(), questions, updatedAt: now } : set));
      SuccessToast('Saved', 'Question set updated');
    } else {
      persist([{ id: `${Date.now()}`, name: name.trim(), description: description.trim(), questions, createdAt: now, updatedAt: now }, ...sets]);
      SuccessToast('Saved', 'Question set created');
    }
    clearForm();
  };

  const editSet = (set: QuestionSet) => {
    setEditingId(set.id);
    setName(set.name);
    setDescription(set.description);
    setQuestionsText(set.questions.join('\n'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteSet = (id: string) => {
    if (!window.confirm('Delete this question set?')) return;
    persist(sets.filter((set) => set.id !== id));
    if (editingId === id) clearForm();
    SuccessToast('Deleted', 'Question set removed');
  };

  const copyQuestions = async (set: QuestionSet) => {
    await navigator.clipboard.writeText(set.questions.join('\n'));
    SuccessToast('Copied', 'Questions copied to clipboard');
  };

  const exportSet = (set: QuestionSet) => {
    const blob = new Blob([set.questions.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${set.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-questions.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <InterviewSidebar>
      <div className="row">
        <div className="col-lg-5" style={{ marginBottom: '24px' }}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>{editingId ? 'Edit Question Set' : 'Create Question Set'}</h3>
            <p style={{ ...styles.secondaryText, marginTop: '-8px' }}>Paste one interview question per line. Jenny should ask these one at a time.</p>

            <label style={fieldLabel}>Set name</label>
            <input style={styles.modalInput} value={name} onChange={(e) => setName(e.target.value)} placeholder="Senior Developer Interview" />

            <label style={{ ...fieldLabel, marginTop: '14px' }}>Description</label>
            <textarea style={{ ...styles.modalInput, minHeight: '80px' } as any} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional notes about the role or question focus" />

            <label style={{ ...fieldLabel, marginTop: '14px' }}>Questions</label>
            <textarea style={{ ...styles.modalInput, minHeight: '260px', fontFamily: 'monospace', fontSize: '14px' } as any} value={questionsText} onChange={(e) => setQuestionsText(e.target.value)} placeholder={'Tell me about your relevant experience.\nHow do you handle conflict in a team?\nWhat would you do in your first 30 days?'} />
            <div style={{ ...styles.secondaryText, fontSize: '12px', marginTop: '8px' }}>{questions.length} question{questions.length === 1 ? '' : 's'} ready. Maximum 50 are saved.</div>

            <div style={styles.modalFooter}>
              {editingId && <button type="button" onClick={clearForm} style={{ ...styles.button, ...styles.secondaryButton } as any}>Cancel Edit</button>}
              <button type="button" onClick={saveSet} style={{ ...styles.button, ...styles.primaryButton } as any}>{editingId ? 'Update Set' : 'Save Set'}</button>
            </div>
          </div>
        </div>

        <div className="col-lg-7" style={{ marginBottom: '24px' }}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Question Sets ({sets.length})</h3>
            {sets.length === 0 ? (
              <div style={{ ...styles.emptyState, minHeight: '260px' }}>
                <p style={styles.secondaryText}>No question sets yet. Create one to attach employer-written questions to interview invites.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.tableHeader, width: '30%' }}>Name</th>
                      <th style={{ ...styles.tableHeader, width: '15%', textAlign: 'center' }}>Questions</th>
                      <th style={{ ...styles.tableHeader, width: '30%' }}>Description</th>
                      <th style={{ ...styles.tableHeader, width: '25%', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sets.map((set) => (
                      <tr key={set.id}>
                        <td style={styles.tableCell}><strong style={styles.primaryText}>{set.name}</strong><div style={{ ...styles.secondaryText, fontSize: '12px' }}>Updated {new Date(set.updatedAt).toLocaleDateString()}</div></td>
                        <td style={{ ...styles.tableCell, textAlign: 'center' }}><span style={{ ...styles.badge, background: '#F0F4FF', color: '#0065FF' }}>{set.questions.length}</span></td>
                        <td style={styles.tableCell}>{set.description || '—'}</td>
                        <td style={{ ...styles.tableCell, textAlign: 'center' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <button onClick={() => editSet(set)} style={{ ...styles.button, ...styles.secondaryButton, padding: '6px 10px', fontSize: '12px' } as any}>Edit</button>
                            <button onClick={() => copyQuestions(set)} style={{ ...styles.button, ...styles.secondaryButton, padding: '6px 10px', fontSize: '12px' } as any}>Copy</button>
                            <button onClick={() => exportSet(set)} style={{ ...styles.button, ...styles.secondaryButton, padding: '6px 10px', fontSize: '12px' } as any}>Export</button>
                            <button onClick={() => deleteSet(set.id)} style={{ ...styles.button, ...styles.dangerButton, padding: '6px 10px', fontSize: '12px' } as any}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </InterviewSidebar>
  );
};

export default InterviewQuestionSets;
